// Motor de renderizado de Questionnaire — puerto a hce-chart de
// packages/hce/src/hce/QuestionnaireEngine/QuestionnaireEngine.tsx (Módulo
// H, Grupo 1). A diferencia del original (que delega el renderizado a
// `QuestionnaireForm` de `@medplum/react`, Mantine), este arma su propia
// capa de JSX (`ItemRenderer.tsx`) sobre `useQuestionnaireForm`
// (`@medplum/react-hooks`, dato puro) — ese hook YA implementa
// enableWhen/repeats/initial/paginación/`removeDisabledItems`, no hace
// falta reimplementarlo, solo el renderizado.
//
// Partido en dos componentes a propósito: `useQuestionnaireForm` exige el
// Questionnaire ya resuelto (objeto completo o Reference/<id>), pero acá
// solo se conoce el CÓDIGO — hace falta buscarlo primero por `identifier`
// (`medplum.searchOne`, el hook no sabe hacer eso). No se puede resolver el
// id y recién ahí llamar al hook condicionalmente en el mismo componente
// (rompe las reglas de hooks) — en cambio, `MotorFormulario` busca el
// recurso y solo MONTA `MotorFormularioCargado` (que sí llama al hook, sin
// condición) una vez que lo tiene.
import type { Questionnaire, Encounter, Patient, QuestionnaireResponse, Reference } from '@medplum/fhirtypes';
import { useMedplum } from '@medplum/react-hooks';
import type { JSX } from 'react';
import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useOutletContext } from 'react-router';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import type { PatientChartOutletContext } from '@/paciente/PatientChartPage';
import { fuenteFormularioPdf } from '@logiacare/hce-pdf';
import { EXTRACTORES } from './extractors';
import { LoteTransaccional, recursoRespuesta } from './discretos/transaccionFhir';
import { FormularioQuestionnaire } from './FormularioQuestionnaire';
import { PanelFirmaImpresion } from './PanelFirmaImpresion';

export interface MotorFormularioProps {
  readonly codigo: string;
  readonly subject: Reference<Patient>;
  readonly encounter?: Reference<Encounter>;
  /** Id de un QuestionnaireResponse ya existente — reabre esa nota puntual (ver NotasTab.tsx/HistoriaTab.tsx) en vez de arrancar un formulario en blanco. */
  readonly respuestaId?: string;
  readonly onGuardado?: (response: QuestionnaireResponse) => void;
}

type EstadoBusqueda =
  | { fase: 'cargando' }
  | { fase: 'no-encontrado' }
  | { fase: 'error'; mensaje: string }
  | { fase: 'listo'; questionnaire: Questionnaire; respuestaExistente?: QuestionnaireResponse };

export function MotorFormulario({ codigo, subject, encounter, respuestaId, onGuardado }: MotorFormularioProps): JSX.Element {
  const { t } = useTranslation();
  const medplum = useMedplum();
  const [estado, setEstado] = useState<EstadoBusqueda>({ fase: 'cargando' });

  useEffect(() => {
    let cancelado = false;
    setEstado({ fase: 'cargando' });
    Promise.all([
      medplum.searchOne('Questionnaire', { identifier: `http://logiacare.ec/fhir/formularios-msp|${codigo}` }),
      respuestaId ? medplum.readResource('QuestionnaireResponse', respuestaId) : Promise.resolve(undefined),
    ])
      .then(([q, respuestaExistente]) => {
        if (cancelado) return;
        setEstado(q ? { fase: 'listo', questionnaire: q, respuestaExistente } : { fase: 'no-encontrado' });
      })
      .catch((err: unknown) => {
        if (cancelado) return;
        setEstado({ fase: 'error', mensaje: err instanceof Error ? err.message : String(err) });
      });
    return () => {
      cancelado = true;
    };
  }, [medplum, codigo, respuestaId]);

  if (estado.fase === 'cargando') {
    return <p className="text-sm text-hce-muted">{t('formulario.cargando')}</p>;
  }
  if (estado.fase === 'no-encontrado') {
    return (
      <Alert variant="red">
        <AlertTitle>{t('formulario.noEncontradoTitulo')}</AlertTitle>
        <AlertDescription>{t('formulario.noEncontradoDetalle', { codigo })}</AlertDescription>
      </Alert>
    );
  }
  if (estado.fase === 'error') {
    return (
      <Alert variant="red">
        <AlertTitle>{t('formulario.errorTitulo')}</AlertTitle>
        <AlertDescription>{estado.mensaje}</AlertDescription>
      </Alert>
    );
  }

  return (
    <MotorFormularioCargado
      codigo={codigo}
      questionnaire={estado.questionnaire}
      subject={subject}
      encounter={encounter}
      respuestaExistente={estado.respuestaExistente}
      onGuardado={onGuardado}
    />
  );
}

interface MotorFormularioCargadoProps extends MotorFormularioProps {
  readonly questionnaire: Questionnaire;
  readonly respuestaExistente?: QuestionnaireResponse;
}

function MotorFormularioCargado({ codigo, questionnaire, subject, encounter, respuestaExistente, onGuardado }: MotorFormularioCargadoProps): JSX.Element {
  const { t } = useTranslation();
  const medplum = useMedplum();
  // Solo lo que se acaba de guardar EN ESTA sesión (creación nueva o
  // edición de una "in-progress") — nunca se precarga desde
  // `respuestaExistente`, para no confundir "recién guardado" (dispara
  // firma+PDF nuevos, ver abajo) con "reabriendo una nota ya completada de
  // antes" (que NO debe volver a pedir firma — ver el branch de abajo).
  const [guardado, setGuardado] = useState<QuestionnaireResponse | null>(null);
  // Descartar (2026-09-11, pedido explícito) — abandona la nota SIN
  // guardarla: si ya existía un QuestionnaireResponse "in-progress"
  // (`respuestaExistente`, reabierto desde Notas/Historia/el widget de
  // pendientes del Dashboard) lo pasa a "stopped" para que deje de aparecer
  // ahí — mismo status que ya usa el botón "Descartar" de
  // FormulariosPendientes.tsx. Si es un formulario recién abierto, sin
  // respuesta todavía guardada, no hay nada que persistir: solo se muestra
  // el aviso de descartado.
  const [descartado, setDescartado] = useState(false);
  // Bloqueo de salida sin firmar (pedido explícito de Sue, 2026-09-27) —
  // "Descartar" solo existe ANTES de guardar (ver `handleDiscard` abajo, que
  // solo se ofrece mientras `!guardado`); una vez guardado, la única salida
  // es firmar.
  const [firmado, setFirmado] = useState(false);
  const reportarPendienteFirma = useOutletContext<PatientChartOutletContext | undefined>()?.reportarPendienteFirma;
  const pendienteFirma = guardado !== null && !firmado;
  useEffect(() => {
    reportarPendienteFirma?.(pendienteFirma);
  }, [reportarPendienteFirma, pendienteFirma]);
  useEffect(() => () => reportarPendienteFirma?.(false), [reportarPendienteFirma]);

  const handleDiscard = useCallback(async () => {
    if (respuestaExistente?.id) {
      await medplum.updateResource<QuestionnaireResponse>({ ...respuestaExistente, status: 'stopped' });
    }
    setDescartado(true);
  }, [medplum, respuestaExistente]);

  const handleSubmit = useCallback(
    async (response: QuestionnaireResponse) => {
      // Editar una nota "in-progress" existente actualiza el mismo
      // QuestionnaireResponse (mismo id) en vez de crear uno nuevo. El
      // extractor solo corre en la creación inicial — repetirlo en cada
      // edición duplicaría los recursos derivados (Observation/Condition/
      // etc., ver extractors.ts), que no soportan upsert por su cuenta.
      let resultado: QuestionnaireResponse;
      if (respuestaExistente) {
        resultado = await medplum.updateResource<QuestionnaireResponse>({ ...response, id: respuestaExistente.id });
      } else {
        // El QuestionnaireResponse y los recursos que saca el extractor van en UNA transacción: si algo falla no queda
        // una respuesta guardada con datos derivados a medias, y reintentar no duplica nada.
        const lote = new LoteTransaccional();
        const local = lote.crear<QuestionnaireResponse>(response);
        const extractor = EXTRACTORES[codigo];
        if (extractor) await extractor(response, { medplum, subject, encounter, lote });
        const respuesta = await lote.enviar(medplum);
        resultado = recursoRespuesta(respuesta, 0, local);
      }
      setGuardado(resultado);
      onGuardado?.(resultado);
    },
    [medplum, subject, encounter, codigo, respuestaExistente, onGuardado],
  );

  if (descartado) {
    return (
      <Alert>
        <AlertTitle>{t('formulario.descartadoTitulo')}</AlertTitle>
        <AlertDescription>{t('formulario.descartadoDetalle')}</AlertDescription>
      </Alert>
    );
  }

  if (guardado) {
    return (
      <div className="flex flex-col gap-3">
        <Alert>
          <AlertTitle>{t('formulario.guardadoTitulo')}</AlertTitle>
          <AlertDescription>{t('formulario.guardadoDetalle')}</AlertDescription>
        </Alert>
        <PanelFirmaImpresion
          documentoId={`QuestionnaireResponse/${guardado.id}`}
          tipoDocumento={codigo}
          titulo={questionnaire.title ?? `Formulario ${codigo}`}
          subject={subject}
          onFirmado={async () => setFirmado(true)}
          especificacionBuilder={async () => {
            const paciente = await medplum.readReference(subject);
            return {
              modo: 'questionnaire',
              questionnaire,
              response: guardado,
              fuenteFormulario: fuenteFormularioPdf(codigo),
              paciente: {
                nombre: paciente.name?.[0] ? `${paciente.name[0].given?.join(' ') ?? ''} ${paciente.name[0].family ?? ''}`.trim() : subject.reference ?? '',
                identificador: paciente.identifier?.[0]?.value,
              },
            };
          }}
        />
      </div>
    );
  }

  // Reabriendo una nota ya "completed" de antes (no tocada en esta sesión):
  // solo lectura, sin PanelFirmaImpresion — mostrarlo acá volvería a pedir
  // "Registro MSP" y generaría OTRO PDF firmado/DocumentReference cada vez
  // que alguien solo quiere LEER la nota. Ver el comentario largo de
  // `soloLectura` en FormularioQuestionnaire.tsx.
  if (respuestaExistente?.status === 'completed') {
    return (
      <div className="flex flex-col gap-3">
        <Alert>
          <AlertTitle>{t('formulario.notaCompletadaTitulo')}</AlertTitle>
          <AlertDescription>{t('formulario.notaCompletadaDetalle')}</AlertDescription>
        </Alert>
        <FormularioQuestionnaire questionnaire={questionnaire} codigo={codigo} subject={subject} encounter={encounter} defaultValue={respuestaExistente} soloLectura />
      </div>
    );
  }

  return (
    <FormularioQuestionnaire
      questionnaire={questionnaire}
      codigo={codigo}
      subject={subject}
      encounter={encounter}
      defaultValue={respuestaExistente}
      textoBoton={t('formulario.guardar')}
      textoBotonEnviando={t('formulario.guardando')}
      onSubmit={handleSubmit}
      onDiscard={handleDiscard}
    />
  );
}
